import { plural } from '../wages/format'

// Box types and weights (handoff §7n.14; routes/settings.js). Every figure is
// whole grams, or null for "not set". The errors are the server's own rules
// in its words (boxTypeErrors): the drawer shows them before Save and the
// server refuses the same things. The advice is the prototype's two
// recommendations, amber and never blocking.

export interface BoxTypeUses {
  picking_jobs: number
  stock_groups: number
  order_lines: number
}

export interface BoxType {
  id: string
  name: string
  boxes_per_pallet: number
  net_weight_g: number | null
  min_weight_g: number | null
  max_weight_g: number | null
  active: boolean
  created_at?: string
  updated_at?: string
  uses?: BoxTypeUses
  rename_blocked?: boolean
  removable?: boolean
}

// "At least 50 g over the net weight" and "a window of at least 100 g" (CONTROL
// V4 boxCheck). Named so a change is one edit.
export const GIVEAWAY_MIN = 50
export const WINDOW_MIN = 100

// Fields as typed; a figure is parsed only to check or send it.
export interface BoxDraft {
  id: string | null
  name: string
  boxes_per_pallet: string
  net_weight_g: string
  min_weight_g: string
  max_weight_g: string
  active: boolean
}

export const WEIGHTS = [
  ['net_weight_g', 'Net weight'],
  ['min_weight_g', 'Minimum'],
  ['max_weight_g', 'Maximum'],
] as const

const MAX_GRAMS = 1000000

export const toBoxDraft = (t: BoxType | null): BoxDraft =>
  t
    ? {
        id: t.id,
        name: t.name,
        boxes_per_pallet: String(t.boxes_per_pallet),
        net_weight_g: t.net_weight_g == null ? '' : String(t.net_weight_g),
        min_weight_g: t.min_weight_g == null ? '' : String(t.min_weight_g),
        max_weight_g: t.max_weight_g == null ? '' : String(t.max_weight_g),
        active: t.active,
      }
    : { id: null, name: '', boxes_per_pallet: '', net_weight_g: '', min_weight_g: '', max_weight_g: '', active: true }

// '' is not set; anything but whole digits is NaN, which the checks name.
export const whole = (s: string): number | null => {
  const v = s.trim()
  if (!v) return null
  return /^\d+$/.test(v) ? Number(v) : NaN
}

export interface BoxValues {
  name: string
  boxes_per_pallet: number | null
  net_weight_g: number | null
  min_weight_g: number | null
  max_weight_g: number | null
  active: boolean
}

export const valuesOf = (d: BoxDraft): BoxValues => ({
  name: d.name.trim(),
  boxes_per_pallet: whole(d.boxes_per_pallet),
  net_weight_g: whole(d.net_weight_g),
  min_weight_g: whole(d.min_weight_g),
  max_weight_g: whole(d.max_weight_g),
  active: d.active,
})

const isSet = (v: number | null | undefined): v is number => v !== null && v !== undefined

export function boxErrors(t: BoxValues): string[] {
  const errs: string[] = []
  if (!t.name.trim()) errs.push('Name the box type.')
  const bpp = t.boxes_per_pallet
  if (!isSet(bpp) || !Number.isInteger(bpp) || bpp < 1 || bpp > 100000) errs.push('Boxes per pallet must be a positive whole number.')
  for (const [k, label] of WEIGHTS) {
    const v = t[k]
    if (isSet(v) && !(Number.isInteger(v) && v > 0 && v <= MAX_GRAMS)) errs.push(`${label} must be a whole number of grams.`)
  }
  if (errs.length) return errs
  const { net_weight_g: net, min_weight_g: min, max_weight_g: max } = t
  if (isSet(net) && isSet(min) && min < net) errs.push(`Minimum must be at least the net weight (${net} g).`)
  if (isSet(min) && isSet(max) && max < min) errs.push(`Maximum must be at least the minimum (${min} g).`)
  if (isSet(net) && !isSet(min)) errs.push('Set a minimum.')
  if (isSet(min) && !isSet(max)) errs.push('Set a maximum.')
  return errs
}

// Shown in amber; a save goes ahead regardless.
export function boxAdvice(t: Pick<BoxValues, 'net_weight_g' | 'min_weight_g' | 'max_weight_g'>): string[] {
  const out: string[] = []
  const { net_weight_g: net, min_weight_g: min, max_weight_g: max } = t
  if (isSet(net) && isSet(min) && min >= net && min - net < GIVEAWAY_MIN) {
    out.push(`At least ${GIVEAWAY_MIN} g over the net weight is recommended (${min - net} g now).`)
  }
  if (isSet(min) && isSet(max) && max >= min && max - min < WINDOW_MIN) {
    out.push(`A window of at least ${WINDOW_MIN} g is recommended (${max - min} g now).`)
  }
  return out
}

// The acceptance window as Packright reads it at weighing.
export function tolerance(t: Pick<BoxValues, 'net_weight_g' | 'min_weight_g' | 'max_weight_g'>): string {
  const { net_weight_g: net, min_weight_g: min, max_weight_g: max } = t
  if (!isSet(net) && !isSet(min) && !isSet(max)) return 'Thresholds not set'
  const bad = [net, min, max].some((v) => isSet(v) && !(Number.isInteger(v) && v > 0))
  if (bad || (isSet(net) && !isSet(min)) || (isSet(min) && !isSet(max))) return 'Thresholds invalid'
  if ((isSet(net) && isSet(min) && min < net) || (isSet(min) && isSet(max) && max < min)) return 'Thresholds invalid'
  if (!isSet(min)) return `Accept up to ${max} g`
  if (!isSet(net)) return `Accept ${min}–${max} g`
  return `Accept ${min}–${max} g · ${min - net} to ${max! - net} g over net`
}

// The table's derived tolerance column (§7n.14), beside its own Min and Max
// columns: the window against the labelled net weight. The handoff's example
// reads "100 g under / 400 g over", but a minimum can never sit under the net
// weight (net ≤ min ≤ max), so both figures are over net.
export function band(t: Pick<BoxValues, 'net_weight_g' | 'min_weight_g' | 'max_weight_g'>): string {
  const line = tolerance(t)
  if (!line.startsWith('Accept')) return line
  const { net_weight_g: net, min_weight_g: min, max_weight_g: max } = t
  if (!isSet(net) || !isSet(min) || !isSet(max)) return 'No net weight to measure from'
  return `${min - net} to ${max - net} g over net`
}

export const grams = (v: number | null | undefined) => (isSet(v) ? `${v} g` : 'Not set')

export function usesText(u: BoxTypeUses | undefined): string {
  if (!u) return 'Not used yet'
  const parts = []
  if (u.picking_jobs) parts.push(`${plural(u.picking_jobs, 'picking job', 'picking jobs')}`)
  const byName = u.stock_groups + u.order_lines
  if (byName) parts.push(`${byName} stock group or order line${byName === 1 ? '' : 's'}`)
  return parts.length ? `Used by ${parts.join(' · ')}` : 'Not used yet'
}

// An existing type sends only what changed; a new one sends every field.
export function boxBody(d: BoxDraft, saved: BoxType | null): Record<string, unknown> {
  const v = valuesOf(d)
  if (!saved) return { ...v }
  const body: Record<string, unknown> = {}
  for (const k of ['name', 'boxes_per_pallet', 'net_weight_g', 'min_weight_g', 'max_weight_g', 'active'] as const) {
    if (v[k] !== saved[k]) body[k] = v[k]
  }
  return body
}
